/*
 * ===============================================================================================================
 *                                Copyright 2020, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import Users from './Users';
import Machines from './Machines';
// import IdentityProviders from './IdentityProvider';
import Roles from './Roles';
import PasswordPolicy from '../components/PasswordPolicies/PasswordPolicies';
import Settings from './Settings';
import React, { useState, Fragment, useEffect } from 'react';
import { Box, Button, Tab, Tabs, Typography, IconButton, Grid, List, ListItem, ListItemText } from '@mui/material';
import {
  LuiModal,
  LuiPageHeader,
  LuiPagination
  // LuiToggleButtonGroup,
  // Tokens
} from '@jda/lui-common-component-library-mui5';
import { useSelector } from 'react-redux';
import { getFeatureFlag as useFeatureFlag } from '@jda/lui-portal-utilities';
import { selectAllAssignedPermissions, selectResourcePermissions } from '../store/auth/authSelector';
import useStyle from '../styles';
import { checkPermission, checkResourcePermission } from '../shared/CheckPermissions/CheckPermission';
import { permissions, resources, actions } from '../shared/CheckPermissions/constants';
import {
  HEADING_TEXT,
  SELECT_LOCATION,
  // LOCATION_TOOLTIP,
  // REALM,
  LOCATION,
  CANCEL_BTN_TEXT,
  SELECT,
  NO_PERMISSION,
  AdminRealmIdList,
  EXPORT_SUCCESS_MESSAGE
} from './constants';
import { HARNESS_FEATURE_FLAG } from '../utility/constants';
import { useDispatch } from 'react-redux';
import { getAuthDetails, getAuthPolicyDetails } from '../store/auth/getAuthDetails';
import Loader from '../shared/Loader';
import { selectAppLoader, selectCurrentSelectedTab } from '../store/appLoader/appLoaderSelector';
import RoleMapping from './RoleMappings';
// import BuiltinPolicies from './BuiltinPolicies';
import Locations from './Locations';
import CustomSearch from '../components/CustomSearch';
import { getUserTokenRealmId, isPortalV2 } from '../utility/commonUtility';
import useWindowDimensions from '../utility/useWindowDimensions';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { selectExported } from '../store/role/roleSelector';
import { fetchExportSuccess } from '../store/role/roleActionCreator';
import { selectAppLocation } from '../store/appLocation/appLocationSelector';
import { assignAppLocation, setActiveButtonIndex } from '../store/appLocation/appLocationActionCreator';
import { getLocations, getLocationsBasedOnSubject } from '../services/locations';
import { Location } from '../models/Locations/Locations';
import { getRealmDetails, getRealmSettings } from '../store/realmSettings/realmSettings';
import { selectRealmInfo, selectRealmSettings } from '../store/realmSettings/realmSettingsSelector';
import UserGroup from './UserGroup';
import { setSelectedTab } from '../store/appLoader/appLoaderActionCreator';
import { fetchIdps } from '../store/user/users';
import { MESSAGES } from '../components/UserDetails/constant';
import { displaySnackbar } from '../store/snackBar/snackBarActionCreator';
import { NATIVE_CAMEL_CASE, NATIVE_UPPER_CASE } from '../container/Settings/constants';
import { MIGRATION_STATUS } from '../components/AddMachineDialog/contants';

interface ModalProps {
  onClose: () => void;
}

export const LocationDialog = ({ onClose }: ModalProps) => {
  const { classes } = useStyle();
  const dispatch = useDispatch();
  const { height } = useWindowDimensions();

  const appLocation = useSelector(selectAppLocation);
  const assignedPermissions = useSelector(selectAllAssignedPermissions);

  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [locationResponse, setLocationResponse] = useState<any>();
  const [selectedLocation, setSelectedLocation] = useState(appLocation);

  const realmSettingsReadWritePermission = Boolean(
    assignedPermissions &&
      assignedPermissions.permissions &&
      checkPermission([permissions.realm_settings.read_write], assignedPermissions.permissions)
  );

  useEffect(() => {
    let ignore = false;
    if (realmSettingsReadWritePermission) {
      // only admin can see all locations list
      getLocations({ pageNumber: page, ...(searchValue && { filterByLocationName: searchValue }) })
        .then(locationResponse => {
          if (!ignore) {
            setLocationResponse(locationResponse);
          }
        })
        .finally(() => {
          if (!ignore) {
            setIsLoading(false);
          }
        });
    } else {
      // as now this api not supporting pagination it will list all available locations in one page
      getLocationsBasedOnSubject({ ...(searchValue && { filterByLocationName: searchValue }) })
        .then(locationResponse => {
          if (!ignore) {
            setLocationResponse(locationResponse || []);
          }
        })
        .finally(() => {
          if (!ignore) {
            setIsLoading(false);
          }
        });
    }
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchValue]);

  return (
    <LuiModal
      isOpen={true}
      modalSize="xs"
      modalTitle={SELECT_LOCATION}
      bottomDivider={false}
      handleClose={onClose}
      secondaryAction={
        <Button
          variant="outlined"
          color="primary"
          onClick={() => {
            dispatch(assignAppLocation(selectedLocation));
            dispatch(setActiveButtonIndex(selectedLocation === null ? 0 : 1));
            dispatch(setSelectedTab(null));
            onClose();
          }}
        >
          {CANCEL_BTN_TEXT}
        </Button>
      }
      primaryAction={
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            dispatch(assignAppLocation(selectedLocation));
            onClose();
          }}
        >
          {SELECT}
        </Button>
      }
      className={classes.locationDialog}
    >
      <Box className={classes.locationDialogToolbar}>
        <Box className={classes.locationSearchField}>
          <CustomSearch
            placeHolder={LOCATION}
            value={searchValue}
            onSearchChange={(searchValue: string) => {
              setSearchValue(searchValue);
              setPage(1);
            }}
          />
        </Box>
      </Box>
      <List
        component="div"
        className={classes.locationList}
        style={{ maxHeight: height - 75 * 8 }}
        data-testid="location-list"
      >
        {/* <ListItem
          button={true}
          selected={selectedLocation === null}
          divider={true}
          onClick={() => {
            setSelectedLocation(null);
          }}
        >
          <ListItemText primary={REALM} />
        </ListItem> */}
        {locationResponse?.data.map((location: Location) => (
          <ListItem
            key={location.id}
            button={true}
            selected={location.id === selectedLocation?.id}
            divider={true}
            onClick={() => {
              setSelectedLocation(location);
            }}
          >
            <ListItemText primary={location.displayName} />
          </ListItem>
        ))}
      </List>
      {locationResponse && locationResponse.metadata && (
        <Box className={classes.locationPagination}>
          <LuiPagination
            totalRows={locationResponse.metadata.totalCount}
            page={page - 1}
            rowsPerPage={locationResponse.metadata.pageSize}
            onPageChange={(_event, page) => {
              setPage(page + 1);
            }}
          />
        </Box>
      )}
      {isLoading && <Loader showLoader={true} isCenter={true} customClasses={classes.locationDialogLoader} />}
    </LuiModal>
  );
};

interface TabPanelProps {
  children: React.ReactNode;
  isActive: boolean;
  [propName: string]: any;
}

const TabPanel = ({ children, isActive, ...other }: TabPanelProps) => {
  return (
    <Typography component="div" role="tabpanel" hidden={!isActive} {...other}>
      {isActive && <>{children}</>}
    </Typography>
  );
};

export const ModulesContainer = () => {
  const [showMessageBox, setShowMessageBox] = useState(true);
  const { classes } = useStyle();
  const dispatch = useDispatch();
  const showLoader = useSelector(selectAppLoader);
  const appLocation = useSelector(selectAppLocation);
  const selectedTabFromReducer = useSelector(selectCurrentSelectedTab);
  // const activeButtonIndex = useSelector(selectActiveButtonIndex);
  const [isAdminRealm, setIsAdminRealm] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNativeIDPEnabled, setIsNativeIDPEnabled] = useState(false);
  const assignedPermissions = useSelector(selectAllAssignedPermissions);
  const resourcePermissions = useSelector(selectResourcePermissions);
  const rolesExported = useSelector(selectExported);
  const realmSettings = useSelector(selectRealmSettings);
  const realmDetails = useSelector(selectRealmInfo);
  // const areBuiltinPoliciesEnabled = useFeatureFlag(HARNESS_FEATURE_FLAG.builtinPolicies);
  const userGroupFeatureFlag = useFeatureFlag(HARNESS_FEATURE_FLAG.userGroup);
  const graviteeFeatureFlag = useFeatureFlag(HARNESS_FEATURE_FLAG.graviteeEnablement);
  const isGraviteeFFEnabled = isPortalV2() ? true : graviteeFeatureFlag;
  const isLocationEnabled = useFeatureFlag(HARNESS_FEATURE_FLAG.location);
  const isUserGroupEnabled = isPortalV2() ? true : userGroupFeatureFlag;
  const modules: any = [];

  const handleChange = (_event: React.SyntheticEvent, newValue: any) => {
    dispatch(setSelectedTab(newValue));
  };
  useEffect(() => {
    getAuthDetails(dispatch, appLocation?.name);
    getAuthPolicyDetails(dispatch, appLocation?.name);
  }, [dispatch, appLocation]);

  const getRealmSettingsDetails = async () => {
    await getRealmSettings(dispatch);
    await getRealmDetails(dispatch);
  };
  const getIdps = async () => {
    const idps = await fetchIdps(dispatch).catch((_error: any) => {
      dispatch(displaySnackbar(MESSAGES.ERROR_IN_FETCHING_IDENTITY_PROVIDER_NAME, 'error'));
    });
    if (idps && idps.length > 0) {
      const nativeIdp = idps.find((idp: any) => idp.idpType === NATIVE_CAMEL_CASE || idp.type === NATIVE_UPPER_CASE);
      setIsNativeIDPEnabled(!!nativeIdp);
    }
  };
  useEffect(() => {
    getUserTokenRealmId().then(userTokenRealmId => {
      setIsAdminRealm(AdminRealmIdList.some(({ realmId }) => userTokenRealmId === realmId));
    });
    getRealmSettingsDetails();
    getIdps();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const locationViewPermission =
    resourcePermissions &&
    resourcePermissions.resources &&
    checkResourcePermission(actions.VIEW, resourcePermissions.resources[resources.LOCATIONS]);

  const userGroupViewPermission =
    resourcePermissions &&
    resourcePermissions.resources &&
    checkResourcePermission(actions.VIEW, resourcePermissions.resources[resources.GROUPS]);

  if (assignedPermissions && assignedPermissions.permissions) {
    if (
      checkPermission(
        [permissions.user.read, permissions.user.read_write, permissions.subject.read_assign],
        assignedPermissions.permissions
      )
    ) {
      modules.push(Users);
    }

    if (isUserGroupEnabled && userGroupViewPermission) {
      modules.push(UserGroup);
    }

    if (
      !appLocation &&
      checkPermission([permissions.machine.read, permissions.machine.read_write], assignedPermissions.permissions)
    ) {
      modules.push(Machines);
    }

    // if (
    //   !appLocation &&
    //   isPortalV2() &&
    //   resourcePermissions &&
    //   resourcePermissions.resources &&
    //   checkResourcePermission(actions.VIEW, resourcePermissions.resources[resources.IDENTITY_PROVIDER])
    // ) {
    //   modules.push(IdentityProviders);
    // }

    // if (!appLocation && areBuiltinPoliciesEnabled && isAdminRealm) {
    //   modules.push(BuiltinPolicies);
    // }

    if (
      checkPermission(
        [permissions.role.read, permissions.role.read_write, permissions.role.read_assign],
        assignedPermissions.permissions
      )
    ) {
      modules.push(Roles);
    }

    if (!appLocation && isLocationEnabled && realmSettings?.enableLocationSegregation && locationViewPermission) {
      modules.push(Locations);
    }

    if (
      realmDetails?.migrationState !== MIGRATION_STATUS.MODERN &&
      !appLocation &&
      checkPermission([permissions.idp_role.read, permissions.idp_role.read_write], assignedPermissions.permissions)
    ) {
      modules.push(RoleMapping);
    }

    if (
      isGraviteeFFEnabled &&
      isNativeIDPEnabled &&
      !appLocation &&
      resourcePermissions &&
      resourcePermissions.resources &&
      checkResourcePermission(actions.VIEW, resourcePermissions.resources[resources.PASSWORD_POLICY])
    ) {
      modules.push(PasswordPolicy);
    }
    if (!appLocation) {
      modules.push(Settings);
    }
  }

  const selectedTab = modules.some((module: { name: string }) => module.name === selectedTabFromReducer)
    ? selectedTabFromReducer
    : modules[0]?.name;

  const handleCloseMessageBox = () => {
    setShowMessageBox(false);
    dispatch(fetchExportSuccess(null));
  };

  const getExportingMessageBox = () => {
    return (
      <Grid
        container
        data-testid="exporting-message-box"
        classes={{ root: `${classes.exportingMessageBox} ${classes.exportingMessageBoxSuccess}` }}
      >
        <Grid item xs={11} classes={{ root: classes.aligning }}>
          <CheckCircleOutlineIcon className={classes.checkedIcon} />
          <Typography variant="body1" className={classes.exportingMessage}>
            {EXPORT_SUCCESS_MESSAGE}
          </Typography>
        </Grid>

        <Grid item xs={1} classes={{ root: classes.aligning }} style={{ justifyContent: 'flex-end' }}>
          <IconButton onClick={handleCloseMessageBox} classes={{ root: classes.closeButton }}>
            <CloseIcon />
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  return (
    <Fragment>
      <Box className={classes.root}>
        <LuiPageHeader title={HEADING_TEXT} subtitle={''} isCollapsable={false} removeBackButton={true}></LuiPageHeader>
        {/* <Box className={classes.headingAlignment}>
            {locationViewPermission && isLocationEnabled && realmSettings?.enableLocationSegregation ? (
              <>
                {appLocation?.displayName ?? HEADING_TEXT}
                {activeButtonIndex !== null && (
                  <Tooltip title={LOCATION_TOOLTIP} arrow placement={'top'}>
                    <LuiToggleButtonGroup
                      className={classes.buttonGroup}
                      onButtonChange={(_button, index) => {
                        dispatch(setActiveButtonIndex(index));
                        if (index === 1) {
                          setIsModalOpen(true);
                        } else {
                          dispatch(assignAppLocation(null));
                        }
                      }}
                      buttonGroupData={[
                        {
                          id: 1,
                          value: 'Realm'
                        },
                        {
                          id: 2,
                          value: 'Sub-Realm'
                        }
                      ]}
                      activeButtonIndex={activeButtonIndex}
                      hasMultipleSelection={false}
                    />
                  </Tooltip>
                )}
              </>
            ) : (
              HEADING_TEXT
            )}
          </Box> */}
        {rolesExported && rolesExported.jobId && showMessageBox ? getExportingMessageBox() : null}
        {!showLoader && modules && modules.length === 0 && <Box>{NO_PERMISSION}</Box>}
        {modules && modules.length > 0 && (
          <Tabs
            className={classes.tabs}
            value={selectedTab}
            onChange={handleChange}
            aria-label={HEADING_TEXT}
            variant="scrollable"
          >
            {modules.map((module: any) => (
              <Tab
                label={module.name}
                value={module.name}
                key={module.name}
                sx={{ minWidth: '160px' }}
                // classes={{ root: classes.text }}
              />
            ))}
          </Tabs>
        )}

        {modules &&
          modules.length > 0 &&
          modules.map((module: any, key: any) => (
            <TabPanel
              isActive={module.name === selectedTab}
              id={`simple-tabpanel-${key}`}
              className={classes.tabPanel}
              key={module.name}
            >
              <module.component />
            </TabPanel>
          ))}
      </Box>
      {isModalOpen && (
        <LocationDialog
          onClose={() => {
            setIsModalOpen(false);
          }}
        />
      )}
    </Fragment>
  );
};
